/**
 * Reads gamepads. The Gamepad API has no events for buttons: the pads' state is polled
 * (navigator.getGamepads()) and compared with the previous read, and every change becomes an
 * edge — in a race once per frame, right before the simulation steps (GameController).
 *
 * - An edge carries the moment the pad says it changed (Gamepad.timestamp, performance.now()
 *   timebase) when that is believable: not in the future and not before the previous read
 *   (the change would have been seen then). Otherwise — a browser with another clock, the
 *   first read — the time of the read. JumpInput then puts a press on the tick it happened in.
 * - Analog triggers press at TRIGGER_PRESS and let go below TRIGGER_RELEASE: a trigger held
 *   half-way never flickers into a string of jumps.
 * - The left stick reads as four direction buttons (PAD.STICK_*), one at a time, with the same
 *   kind of hysteresis.
 * - The D-pad and the stick repeat while held (like a held arrow key): moving the focus down a
 *   long list needs no hammering.
 * - A pad that goes away (unplugged, flat, another pad in its slot) lets go of all it held.
 * - Within a read, presses come before releases: going from A to RT in one frame never lets
 *   go of the jump in between.
 */
import { PAD, type PadStyle, padDirection, padStyle } from './pads';

export interface PadEdge {
  /** Gamepad.index of the pad. */
  pad: number;
  /** A button of the standard layout, or a direction of the left stick (PAD.STICK_*). */
  button: number;
  /** Pressed (true) or let go. */
  down: boolean;
  /** A held direction repeating (D-pad, stick); never for other buttons. */
  repeat: boolean;
  /** When it happened (performance.now() timebase). */
  time: number;
  /** How the pad names its buttons. */
  style: PadStyle;
}

/** What navigator.getGamepads() returns: a slot per pad, null where there is none. */
export type GamepadList = ArrayLike<Gamepad | null>;

/** Triggers (and any other analog button): pressed from here… */
export const TRIGGER_PRESS = 0.4;
/** …and let go below this. */
export const TRIGGER_RELEASE = 0.25;
/** The stick points somewhere from here (either axis)… */
export const STICK_PRESS = 0.5;
/** …and stops pointing there below this. */
export const STICK_RELEASE = 0.3;
/** A held direction repeats after this long… */
export const REPEAT_DELAY_MS = 400;
/** …then this often. */
export const REPEAT_MS = 130;
/** Buttons read per pad (the standard layout has 17, a PlayStation pad one more). */
const MAX_BUTTONS = 32;

interface PadState {
  id: string;
  style: PadStyle;
  /** Buttons held as of the last read. */
  held: Uint8Array;
  /** The stick's direction (PAD.STICK_*), or -1. */
  stick: number;
  /** The direction that repeats (the last one pressed), or -1. */
  repeating: number;
  repeatAt: number;
}

export class PadReader {
  private readonly pads = new Map<number, PadState>();
  private readonly seen = new Set<number>();
  private lastRead = Number.NaN;

  /** Pads connected as of the last read. */
  get connected(): number {
    return this.pads.size;
  }

  /** Compares `list` (read at `now`) with the previous read; appends the changes to `out`. */
  read(list: GamepadList, now: number, out: PadEdge[]): void {
    const since = Number.isNaN(this.lastRead) ? now : this.lastRead;
    this.lastRead = now;
    const seen = this.seen;
    seen.clear();
    for (let i = 0; i < list.length; i++) {
      const pad = list[i];
      if (!pad || pad.connected === false || seen.has(pad.index)) continue;
      seen.add(pad.index);
      let state = this.pads.get(pad.index);
      if (state && state.id !== pad.id) {
        // Another pad in the slot: the old one let go of everything.
        this.letGo(pad.index, state, now, out);
        state = undefined;
      }
      if (!state) {
        state = {
          id: pad.id,
          style: padStyle(pad.id),
          held: new Uint8Array(MAX_BUTTONS),
          stick: -1,
          repeating: -1,
          repeatAt: 0,
        };
        this.pads.set(pad.index, state);
      }
      this.readPad(pad, state, padTime(pad.timestamp, since, now), now, out);
    }
    for (const [index, state] of this.pads) {
      if (seen.has(index)) continue;
      this.letGo(index, state, now, out);
      this.pads.delete(index);
    }
  }

  private readPad(pad: Gamepad, state: PadState, time: number, now: number, out: PadEdge[]): void {
    const index = pad.index;
    const buttons = pad.buttons;
    const count = Math.min(buttons.length, MAX_BUTTONS);
    const held = state.held;
    const stickBefore = state.stick;
    const stick = stickDirection(pad.axes[0] ?? 0, pad.axes[1] ?? 0, stickBefore);
    state.stick = stick;
    let pressed = false;
    // Presses first (see the file comment), then releases.
    for (let b = 0; b < count; b++) {
      if (held[b] === 1 || !isDown(buttons[b], false)) continue;
      held[b] = 1;
      this.press(state, index, b, time, now, out);
      pressed ||= padDirection(b) !== null;
    }
    if (stick !== stickBefore && stick !== -1) {
      this.press(state, index, stick, time, now, out);
      pressed = true;
    }
    for (let b = 0; b < MAX_BUTTONS; b++) {
      if (held[b] === 0 || (b < count && isDown(buttons[b], true))) continue;
      held[b] = 0;
      this.release(state, index, b, time, out);
    }
    if (stick !== stickBefore && stickBefore !== -1) {
      this.release(state, index, stickBefore, time, out);
    }
    // A direction held on repeats, one step per read at most (no bursts after a stall).
    if (!pressed && state.repeating !== -1 && now >= state.repeatAt) {
      state.repeatAt = now + REPEAT_MS;
      out.push(edge(index, state, state.repeating, true, true, now));
    }
  }

  private press(
    state: PadState,
    pad: number,
    button: number,
    time: number,
    now: number,
    out: PadEdge[],
  ): void {
    if (padDirection(button) !== null) {
      state.repeating = button;
      state.repeatAt = now + REPEAT_DELAY_MS;
    }
    out.push(edge(pad, state, button, true, false, time));
  }

  private release(
    state: PadState,
    pad: number,
    button: number,
    time: number,
    out: PadEdge[],
  ): void {
    if (state.repeating === button) state.repeating = -1;
    out.push(edge(pad, state, button, false, false, time));
  }

  private letGo(pad: number, state: PadState, now: number, out: PadEdge[]): void {
    for (let b = 0; b < MAX_BUTTONS; b++) {
      if (state.held[b] === 0) continue;
      state.held[b] = 0;
      this.release(state, pad, b, now, out);
    }
    if (state.stick !== -1) {
      const stick = state.stick;
      state.stick = -1;
      this.release(state, pad, stick, now, out);
    }
  }
}

/**
 * When a change between the previous read (`since`) and this one (`now`) happened: the pad's
 * own timestamp if it falls in between, else `now`.
 */
export function padTime(stamp: number, since: number, now: number): number {
  return Number.isFinite(stamp) && stamp >= since && stamp <= now ? stamp : now;
}

/** Whether a button counts as held; analog ones with hysteresis (see TRIGGER_PRESS). */
function isDown(button: GamepadButton | undefined, wasDown: boolean): boolean {
  if (!button) return false;
  const value = button.value;
  if (value > 0 && value < 1) return wasDown ? value >= TRIGGER_RELEASE : value >= TRIGGER_PRESS;
  return button.pressed;
}

/**
 * The direction the left stick points in (PAD.STICK_*, or -1): its main axis, once past
 * STICK_PRESS; a direction already held stays until the stick is back below STICK_RELEASE.
 */
function stickDirection(x: number, y: number, current: number): number {
  const sx = Number.isFinite(x) ? x : 0;
  const sy = Number.isFinite(y) ? y : 0;
  if (current !== -1 && along(current, sx, sy) >= STICK_RELEASE) return current;
  if (Math.max(Math.abs(sx), Math.abs(sy)) < STICK_PRESS) return -1;
  if (Math.abs(sx) > Math.abs(sy)) return sx < 0 ? PAD.STICK_LEFT : PAD.STICK_RIGHT;
  return sy < 0 ? PAD.STICK_UP : PAD.STICK_DOWN;
}

/** How far the stick points in a direction (up is negative y). */
function along(direction: number, x: number, y: number): number {
  switch (direction) {
    case PAD.STICK_UP:
      return -y;
    case PAD.STICK_DOWN:
      return y;
    case PAD.STICK_LEFT:
      return -x;
    default:
      return x;
  }
}

function edge(
  pad: number,
  state: PadState,
  button: number,
  down: boolean,
  repeat: boolean,
  time: number,
): PadEdge {
  return { pad, button, down, repeat, time, style: state.style };
}
